import { useState, useRef, useEffect } from "react";
import { FileText, Copy, FileDown, FileType, FileCode2, Check, Image as ImageIcon, Download } from "lucide-react";
import JSZip from "jszip";
import html2pdf from "html2pdf.js";
import { Tip } from "@/components/Tip";
import { MarkdownBlock, copyText } from "@/components/MarkdownBlock";
import { mapCitationTokensAsLinks } from "@/lib/citations";
import { dedupeSources } from "@/lib/sources";
import { SourceChip } from "@/components/SourcesPanel";
import { cn } from "@/lib/utils";
import { fileDownloadUrl } from "@/lib/api";

export interface SourceItem {
  title: string;
  url?: string;
  domain?: string;
  sourceId?: string;
  /** Sources UX (additive): provider-supplied snippet, never fabricated. */
  snippet?: string;
  /** Sources UX (additive): ISO date YYYY-MM-DD, provider-supplied only. */
  publishedAt?: string;
  /** WG-4B (additive): reader source marker — "web" | "pdf". */
  contentType?: string;
}

export interface MessageAttachment {
  kind: string;
  name: string;
  mime?: string | null;
  size?: number | null;
  data_url?: string | null;
  text?: string | null;
  file_id?: string;
}

export interface ThreadMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  sources?: SourceItem[];
  model?: string;
  createdAt?: string;
  attachments?: MessageAttachment[];
}

interface ChatThreadProps {
  messages: ThreadMessage[];
  isLoading?: boolean;
  /**
   * Sources UX: message id whose sources panel is currently open (chip
   * aria-expanded state), or null.
   */
  openSourcesMsgId?: string | null;
  /** Chip click / citation [n] click → open panel (optionally highlight row).
   *  messageId=null closes the panel (chip toggle). */
  onOpenSources?: (messageId: string | null, sources: SourceItem[], highlightIndex?: number | null) => void;
}

// ---------- Экспорт ----------

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeXml(text: string): string {
  return escapeHtml(text);
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function filenameFor(title: string, ext: string): string {
  const safe = title.replace(/[^\wа-яА-ЯёЁ\s-]/g, "").trim().slice(0, 50) || "chat";
  return `${safe}.${ext}`;
}

async function downloadMarkdown(content: string, title: string) {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  saveBlob(blob, filenameFor(title, "md"));
}

async function downloadDocx(content: string, title: string) {
  const zip = new JSZip();
  zip.file(
    "[Content_Types].xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`
  );
  zip.file(
    "_rels/.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`
  );
  const paragraphs = content
    .split("\n")
    .map((line) => {
      const esc = escapeXml(line);
      return `<w:p><w:r><w:t xml:space="preserve">${esc}</w:t></w:r></w:p>`;
    })
    .join("");
  zip.file(
    "word/document.xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
<w:body>${paragraphs}</w:body>
</w:document>`
  );
  const blob = await zip.generateAsync({ type: "blob" });
  saveBlob(blob, filenameFor(title, "docx"));
}

function downloadPdf(content: string, title: string) {
  const el = document.createElement("div");
  el.style.position = "fixed";
  el.style.left = "-9999px";
  el.style.top = "0";
  el.style.width = "640px";
  el.style.background = "#ffffff";
  el.style.color = "#111111";
  el.style.padding = "24px";
  el.style.fontFamily = "Geist, Arial, sans-serif";
  el.style.fontSize = "14px";
  el.style.lineHeight = "1.6";
  el.innerHTML = `<h1 style="font-size:20px;margin-bottom:16px;">${escapeHtml(title)}</h1><div style="white-space:pre-wrap;">${escapeHtml(content)}</div>`;
  document.body.appendChild(el);
  html2pdf()
    .set({
      margin: 12,
      filename: filenameFor(title, "pdf"),
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .from(el)
    .save()
    .then(() => el.remove())
    .catch(() => el.remove());
}

// ---------- Вспомогательные ----------

function formatTimestamp(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

// ---------- Компоненты ----------

function IconActionButton({
  onClick,
  icon,
  title,
  className,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  return (
    <Tip label={title} side="top">
      <button
        onClick={async () => {
          await onClick();
          setDone(true);
          setTimeout(() => setDone(false), 1600);
        }}
        className={cn(
          "h-8 w-8 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg transition-colors inline-flex items-center justify-center",
          className
        )}
        aria-label={title}
      >
        {done ? <Check size={14} className="text-accent" /> : icon}
      </button>
    </Tip>
  );
}

function ExportMenu({ content, title, className }: { content: string; title: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Tip label="Сохранить как" side="top">
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            "h-8 w-8 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg transition-colors inline-flex items-center justify-center",
            className
          )}
          aria-label="Сохранить как"
        >
          <FileDown size={14} />
        </button>
      </Tip>
      {open && (
        <div className="absolute left-0 bottom-full mb-1 w-44 bg-elevated border border-strongline rounded-lg shadow-lg overflow-hidden z-20 py-1">
          <button
            onClick={() => {
              downloadPdf(content, title);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-hoverbg transition-colors"
          >
            <FileType size={14} className="text-red-400" />
            <span>PDF</span>
          </button>
          <button
            onClick={() => {
              downloadMarkdown(content, title);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-hoverbg transition-colors"
          >
            <FileCode2 size={14} className="text-accent" />
            <span>Markdown (.md)</span>
          </button>
          <button
            onClick={() => {
              downloadDocx(content, title);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-hoverbg transition-colors"
          >
            <FileText size={14} className="text-blue-400" />
            <span>Word (.docx)</span>
          </button>
        </div>
      )}
    </div>
  );
}


function AttachmentChip({ att }: { att: MessageAttachment }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-elevated border border-strongline pl-2 pr-2.5 py-1.5 text-xs text-primary">
      {att.data_url && att.kind === "image" ? (
        <img src={att.data_url} alt="" className="w-8 h-8 rounded object-cover" />
      ) : (
        <FileText size={14} className="text-accent shrink-0" />
      )}
      <span className="max-w-[180px] truncate">{att.name}</span>
      {att.size != null && att.size > 0 && (
        <span className="text-tertiary shrink-0">
          {att.size < 1024 ? `${att.size} Б` : att.size < 1024 * 1024 ? `${(att.size / 1024).toFixed(1)} КБ` : `${(att.size / (1024 * 1024)).toFixed(1)} МБ`}
        </span>
      )}
      {att.file_id && (
        <a
          href={fileDownloadUrl(att.file_id)}
          download={att.name}
          className="p-0.5 rounded text-tertiary hover:text-primary transition-colors"
          aria-label={`Скачать ${att.name}`}
        >
          <Download size={12} />
        </a>
      )}
    </div>
  );
}

export function ChatThread({
  messages,
  isLoading,
  openSourcesMsgId = null,
  onOpenSources,
}: ChatThreadProps) {
  if (messages.length === 0) return null;

  return (
    <div className="flex flex-col gap-8">
      {messages.map((message, index) => (
        <div key={message.id} data-msg-id={message.id} className="flex flex-col gap-4">
          {/* User Message — справа */}
          {message.role === "user" && (
            <div className="flex flex-col items-end gap-2 w-full">
              {message.attachments && message.attachments.length > 0 && (
                <div className="flex flex-wrap justify-end gap-2 w-full">
                  {message.attachments.map((a, i) => (
                    <AttachmentChip key={i} att={a} />
                  ))}
                </div>
              )}
              <div className="ml-auto w-fit max-w-full bg-accent/10 border border-accent/20 rounded-2xl rounded-br-md px-4 py-3 md:px-5 md:py-3">
                <h3 className="text-base font-normal text-primary text-right break-words">{message.content}</h3>
              </div>
              <IconActionButton onClick={() => copyText(message.content)} icon={<Copy size={13} />} title="Копировать" />
            </div>
          )}

          {/* Assistant Message */}
          {message.role === "assistant" && (
            <div className="flex flex-col gap-4">
              {/* Sources UX: compact chip (was a horizontal card strip).
                  Opens the right-side Sources panel; count = deduped
                  backend-provided sources only — never a fake number. */}
              {message.sources && message.sources.length > 0 && onOpenSources && (
                <SourceChip
                  count={dedupeSources(message.sources).length}
                  expanded={openSourcesMsgId === message.id}
                  onToggle={() =>
                    openSourcesMsgId === message.id
                      ? onOpenSources(null, message.sources ?? [], null)
                      : onOpenSources(message.id, message.sources ?? [], null)
                  }
                />
              )}

              {/* Answer */}
              <div className="space-y-2 min-w-0 w-full">
                  {!message.content && isLoading ? (
                    <div className="shimmer-text text-lg">Размышление...</div>
                  ) : (
                    <MarkdownBlock
                      content={mapCitationTokensAsLinks(message.content, message.sources)}
                      onCitationClick={
                        onOpenSources && message.sources && message.sources.length > 0
                          ? (n) => onOpenSources(message.id, message.sources ?? [], n)
                          : undefined
                      }
                    />
                  )}
                  {!isLoading && (
                    <div className="flex items-center gap-1 pt-1">
                      <IconActionButton onClick={() => copyText(message.content)} icon={<Copy size={14} />} title="Копировать" />
                      <ExportMenu content={message.content} title={message.model ? `RealityAI_${message.model.split("/").pop()}` : "RealityAI_ответ"} />
                      {formatTimestamp(message.createdAt) && (
                        <span className="ml-2 text-xs text-tertiary">{formatTimestamp(message.createdAt)}</span>
                      )}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}